import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@material-ui/core';
import './ConfirmDialog.css';

// One confirmation dialog for the whole app.
//
// Every screen was reaching for a bare MUI Dialog with a DialogTitle and a
// Yes/No pair, so the same question looked different depending on where it was
// asked — while the logout dialog, three files away, already had the design
// this product uses. This is that design, made reusable.
//
// `fact` is for the single number worth seeing before committing: the shift
// length you are about to close, the time being recorded.
const ConfirmDialog = ({
  open,
  tone,
  icon,
  title,
  message,
  fact,
  factLabel,
  confirmLabel,
  cancelLabel,
  confirmTone,
  busy,
  onConfirm,
  onCancel,
}) => (
  <Dialog
    open={open}
    onClose={busy ? undefined : onCancel}
    aria-labelledby="app-dialog-title"
    classes={{ paper: 'app-dialog-paper' }}
  >
    <div className={`app-dialog app-dialog--${tone}`}>
      {icon && (
        <div className="app-dialog-badge" aria-hidden="true">
          {icon}
        </div>
      )}

      <h2 className="app-dialog-title" id="app-dialog-title">
        {title}
      </h2>

      {message && <p className="app-dialog-text">{message}</p>}

      {fact && (
        <div className="app-dialog-fact">
          <span className="app-dialog-fact-label">{factLabel}</span>
          <span className="app-dialog-fact-value">{fact}</span>
        </div>
      )}

      <div className="app-dialog-buttons">
        <button
          type="button"
          className="app-dialog-btn app-dialog-btn--ghost"
          onClick={onCancel}
          disabled={busy}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={`app-dialog-btn app-dialog-btn--${confirmTone}`}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  </Dialog>
);

ConfirmDialog.propTypes = {
  open: PropTypes.bool,
  // Colours the badge: navy (neutral), success, warning.
  tone: PropTypes.oneOf(['navy', 'success', 'warning']),
  icon: PropTypes.node,
  title: PropTypes.string,
  message: PropTypes.string,
  fact: PropTypes.string,
  factLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmTone: PropTypes.oneOf(['primary', 'danger']),
  // Blocks both buttons and the backdrop while the request is in flight, so a
  // second tap cannot fire a second clock punch.
  busy: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

ConfirmDialog.defaultProps = {
  open: false,
  tone: 'navy',
  icon: null,
  title: '',
  message: '',
  fact: '',
  factLabel: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  confirmTone: 'primary',
  busy: false,
  onConfirm: () => {},
  onCancel: () => {},
};

export default ConfirmDialog;
